import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { usePhoneCall, PhoneNumber } from "@/state/hooks/usePhoneCall";
import {
    PHONE_COUNTRY_CODES,
    sanitizeLocalPhoneInput,
    isValidPhoneNumber,
    extractDigits,
    PhoneCountryCode,
} from "@/common/utils/phone";
import { getPhoneApiErrorMessage } from "@/common/utils/phoneApiErrors";
import PhoneNumberFormFields from "@/features/phone-call-widget/PhoneNumberFormFields";
import { isExtensionValid } from "@/features/phone-call-widget/callerIdExtension";

interface DestinationNumberProps {
    onCancel: () => void;
    onSave: (destination: PhoneNumber) => void;
}

/**
 * DestinationNumberForm component for entering a destination phone number (dark widget variant).
 * This is the number that will receive calls (the practice's internal line).
 */
const DestinationNumberForm = ({ onCancel, onSave }: DestinationNumberProps) => {
    const [countryCodeKey, setCountryCodeKey] = useState<string>("US");
    const [phoneNumber, setPhoneNumber] = useState<string>("");
    const [referenceName, setReferenceName] = useState<string>("");
    const [extension, setExtension] = useState<string>("");
    const numberInputRef = useRef<HTMLInputElement>(null);
    const { addDestinationMutation } = usePhoneCall();

    useEffect(() => {
        if (numberInputRef.current) {
            numberInputRef.current.focus();
        }
    }, []);

    const dialCode = PHONE_COUNTRY_CODES[countryCodeKey as PhoneCountryCode] ?? PHONE_COUNTRY_CODES.US;

    const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPhoneNumber(sanitizeLocalPhoneInput(e.target.value, dialCode));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (
            !phoneNumber ||
            !isValidPhoneNumber(phoneNumber, dialCode) ||
            !isExtensionValid(extension, { capped: true })
        )
            return;

        try {
            const result = await addDestinationMutation.mutateAsync({
                phoneNumber: extractDigits(phoneNumber),
                countryCode: dialCode,
                referenceName: referenceName || "My Phone Number",
                ...(extension.trim() && { extension: extension.trim() }),
            });
            onSave(result);
        } catch (error: unknown) {
            const message = await getPhoneApiErrorMessage(error);
            toast.error(message);
        }
    };

    const isValid = isValidPhoneNumber(phoneNumber, dialCode) && isExtensionValid(extension, { capped: true });

    return (
        <form onSubmit={handleSubmit} className="w-full h-full flex flex-col bg-shadow-950 rounded-lg shadow">
            <div className="px-6 py-5 flex flex-col gap-1 bg-shadow-850">
                <h3 className="text-white text-lg font-semibold">Call Destination Number</h3>
                <p className="text-white text-sm">Enter your phone number (clients won't see it).</p>
            </div>

            <div className="flex-1 bg-shadow-950 px-6 py-5 flex flex-col gap-4">
                <PhoneNumberFormFields
                    variant="dark"
                    idPrefix="destination-widget"
                    countryCodeKey={countryCodeKey}
                    onCountryCodeChange={setCountryCodeKey}
                    phoneNumber={phoneNumber}
                    onPhoneNumberChange={handlePhoneNumberChange}
                    phoneInputRef={numberInputRef}
                    extension={extension}
                    onExtensionChange={setExtension}
                    referenceName={referenceName}
                    onReferenceNameChange={setReferenceName}
                    referenceNamePlaceholder="Staff Number"
                />
            </div>

            <div className="px-6 py-5 bg-shadow-800 flex gap-3 rounded-b-lg">
                <button type="button" onClick={onCancel} className="secondary-button flex-1 !text-max-800">
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={!isValid || addDestinationMutation.isPending}
                    className="primary-button flex-1"
                >
                    {addDestinationMutation.isPending ? "Saving..." : "Save"}
                </button>
            </div>
        </form>
    );
};

export default DestinationNumberForm;
